/**
 * Fold older campaign views into { id, type, name, settings }.
 *
 * Views used to spread their defaults onto the view object itself. Save and
 * restore read view.settings, so those top-level copies were ignored and
 * opening a campaign restored the built-in stubs instead.
 *
 * Field-visibility stubs (showCategory, name/category columns, showCheckbox)
 * are not user choices. A real save, or the campaign-level / table
 * localStorage copy, is kept. Anything else stays unset so restore does not
 * overwrite the campaign with defaults.
 */

const CARD_VIEWS = new Set(['flow', 'kanban', 'neural']);

const SETTING_KEYS = [
  'viewport',
  'filters',
  'fokusMode',
  'flowViewVisibility',
  'swimlaneSetting',
  'sortSetting',
  'ganttSwimlane',
  'viewMode',
  'swimlaneGroupBy',
  'todoSwimlane',
  'todoShowNestedItems',
  'showArchived',
  'showActionableOnly',
  'showNestedCampaigns',
];

const VISIBILITY_KEYS = ['cardDisplaySettings', 'columnVisibility', 'elementVisibility'];

const LOOSE_KEYS = new Set([...SETTING_KEYS, ...VISIBILITY_KEYS]);

const LEGACY_CARD_MARKERS = [
  'showCategory',
  'showPriority',
  'showTags',
  'showDueDate',
  'showCreatedAt',
  'showStartedAt',
  'showCompletedAt',
  'showUpdatedAt',
];

const CURRENT_CARD_MARKERS = [
  'statusDropdown',
  'categoryDropdown',
  'missionIcon',
  'priorityDropdown',
  'dueDate',
  'detailsButton',
  'difficultyDropdown',
];

const LEGACY_TABLE_MARKERS = [
  'name',
  'category',
  'deliverables',
  'inputs',
  'select',
  'dependsOn',
  'actions',
];

const CURRENT_TABLE_MARKERS = [
  'icon',
  'detailsButton',
  'archiveButton',
  'deleteButton',
  'openInViewButton',
  'attachments',
  'estimatedDurationMinutes',
];

const CURRENT_TODO_MARKERS = [
  'icon',
  'status',
  'priority',
  'tags',
  'dueDate',
  'dependencies',
  'difficulty',
];

function isPlainObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function hasAnyKey(value, keys) {
  return keys.some((key) => Object.prototype.hasOwnProperty.call(value, key));
}

export function isLegacyCardDisplaySettings(value) {
  if (!isPlainObject(value)) return false;
  if (hasAnyKey(value, CURRENT_CARD_MARKERS)) return false;
  return hasAnyKey(value, LEGACY_CARD_MARKERS);
}

export function isLegacyTableVisibility(value) {
  if (!isPlainObject(value)) return false;
  if (hasAnyKey(value, CURRENT_TABLE_MARKERS)) return false;
  return hasAnyKey(value, LEGACY_TABLE_MARKERS);
}

export function isLegacyTodoVisibility(value) {
  if (!isPlainObject(value)) return false;
  if (hasAnyKey(value, CURRENT_TODO_MARKERS)) return false;
  return Object.keys(value).some((key) => key.startsWith('show'));
}

function isRealCardDisplaySettings(value) {
  return isPlainObject(value) && Object.keys(value).length > 0 && !isLegacyCardDisplaySettings(value);
}

function isRealTableVisibility(value) {
  return isPlainObject(value) && Object.keys(value).length > 0 && !isLegacyTableVisibility(value);
}

function isRealTodoVisibility(value) {
  return isPlainObject(value) && Object.keys(value).length > 0 && !isLegacyTodoVisibility(value);
}

function firstReal(predicate, ...candidates) {
  return candidates.find((candidate) => predicate(candidate));
}

/**
 * Campaign card settings are either per view ({ flow, kanban, neural })
 * or one shared map from before views were split.
 */
export function campaignCardSettingsForType(cardDisplaySettings, type) {
  if (!CARD_VIEWS.has(type) || !isPlainObject(cardDisplaySettings)) return undefined;
  const isPerView = cardDisplaySettings.flow !== undefined
    || cardDisplaySettings.kanban !== undefined
    || cardDisplaySettings.neural !== undefined;
  if (isPerView) return cardDisplaySettings[type];
  return cardDisplaySettings;
}

function sameJson(a, b) {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function normalizeCampaignView(view, context) {
  if (!isPlainObject(view)) return view;

  const existingSettings = isPlainObject(view.settings) ? view.settings : {};
  const settings = { ...existingSettings };
  const type = view.type;

  SETTING_KEYS.forEach((key) => {
    if (settings[key] === undefined && view[key] !== undefined) {
      settings[key] = view[key];
    }
  });

  if (CARD_VIEWS.has(type)) {
    const picked = firstReal(
      isRealCardDisplaySettings,
      existingSettings.cardDisplaySettings,
      view.cardDisplaySettings,
      campaignCardSettingsForType(context.cardDisplaySettings, type),
    );
    if (picked) settings.cardDisplaySettings = picked;
    else delete settings.cardDisplaySettings;
  }

  if (type === 'table') {
    const picked = firstReal(
      isRealTableVisibility,
      existingSettings.columnVisibility,
      view.columnVisibility,
      context.storedColumnVisibility,
    );
    if (picked) settings.columnVisibility = picked;
    else delete settings.columnVisibility;
  }

  if (type === 'todolist') {
    const picked = firstReal(
      isRealTodoVisibility,
      existingSettings.elementVisibility,
      view.elementVisibility,
    );
    if (picked) settings.elementVisibility = picked;
    else delete settings.elementVisibility;
  }

  const hasLooseKeys = LOOSE_KEYS.size > 0 && [...LOOSE_KEYS].some((key) => view[key] !== undefined);
  const settingsMatch = isPlainObject(view.settings) && sameJson(view.settings, settings);
  if (!hasLooseKeys && settingsMatch) return view;

  const next = {
    id: view.id,
    type: view.type,
    name: view.name,
    createdAt: view.createdAt,
    updatedAt: view.updatedAt,
    settings,
  };
  if (view.isDefault !== undefined) next.isDefault = view.isDefault;

  Object.keys(view).forEach((key) => {
    if (
      key === 'id' || key === 'type' || key === 'name'
      || key === 'createdAt' || key === 'updatedAt' || key === 'isDefault'
      || key === 'settings' || LOOSE_KEYS.has(key)
    ) {
      return;
    }
    next[key] = view[key];
  });

  return next;
}

/**
 * When a campaign is getting its first views, prefer choices already saved on
 * the campaign (or table localStorage) over the fresh defaults.
 * @param {Object[]} views
 * @param {{ cardDisplaySettings?: Object, storedColumnVisibility?: Object }} [context]
 */
export function withSavedFieldVisibility(views, context = {}) {
  if (!Array.isArray(views)) return views;

  let changed = false;
  const next = views.map((view) => {
    if (!isPlainObject(view)) return view;
    const settings = { ...(view.settings || {}) };
    let seeded = false;

    if (CARD_VIEWS.has(view.type)) {
      const picked = firstReal(
        isRealCardDisplaySettings,
        campaignCardSettingsForType(context.cardDisplaySettings, view.type),
      );
      if (picked) {
        settings.cardDisplaySettings = picked;
        seeded = true;
      }
    }

    if (view.type === 'table') {
      const picked = firstReal(isRealTableVisibility, context.storedColumnVisibility);
      if (picked) {
        settings.columnVisibility = picked;
        seeded = true;
      }
    }

    if (!seeded) return view;
    changed = true;
    return { ...view, settings };
  });

  return changed ? next : views;
}

/**
 * @param {Object[]} views
 * @param {{ cardDisplaySettings?: Object, storedColumnVisibility?: Object }} [context]
 * @returns {Object[]} The same array reference when nothing changed
 */
export function normalizeCampaignViews(views, context = {}) {
  if (!Array.isArray(views)) return views;

  let changed = false;
  const next = views.map((view) => {
    const normalized = normalizeCampaignView(view, context);
    if (normalized !== view) changed = true;
    return normalized;
  });

  return changed ? next : views;
}
